import { T, withAlpha } from '../designTokens';
/**
 * ShareModal — Create, view, and revoke share links for a report.
 *
 * Props:
 *   reportId   string  — the report to manage links for
 *   reportName string  — displayed in the header
 *   onClose    ()=>void
 */

import React, { useState } from 'react';
import { useUser } from '@clerk/react';
import { ShareLinkRecord } from '../utils/types';
import { createShareLink, getShareLinks, revokeShareLink, ShareExpiry } from '../utils/api';
import { useApiResource } from '../hooks/useApiResource';
import ApiErrorNotice from './ApiErrorNotice';
import { formatDateTime } from '../utils/date';

interface ShareModalProps {
  reportId: string;
  reportName: string;
  onClose: () => void;
}

const EXPIRY_OPTIONS: { value: ShareExpiry; label: string }[] = [
  { value: '30m',  label: '30 minutes' },
  { value: '1h',   label: '1 hour' },
  { value: '6h',   label: '6 hours' },
  { value: '24h',  label: '24 hours (default)' },
  { value: '7d',   label: '7 days' },
  { value: '30d',  label: '30 days' },
];

const SHARE_LINK_STATUS_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  active:  { bg: T.green100, text: T.green800, dot: T.green600 },
  expired: { bg: T.yellow100, text: T.yellow800, dot: T.gradeB },
  revoked: { bg: T.errorBg, text: T.gradeF, dot: T.error },
};

export default function ShareModal({ reportId, reportName, onClose }: ShareModalProps) {
  const { user } = useUser();
  const { data: links, loading: isLoading, error: fetchError, refetch: fetchLinks } = useApiResource(
    () => getShareLinks(reportId),
    [reportId],
  );
  const [isCreating, setIsCreating] = useState(false);
  const [actionError, setActionError] = useState<Error | null>(null);

  // Create-form state
  const [expiresIn, setExpiresIn] = useState<ShareExpiry>('24h');
  const [label, setLabel] = useState('');

  // Copy-to-clipboard state
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Revoke confirmation state
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!user?.id) {
      setActionError(new Error('You must be signed in to create a share link.'));
      return;
    }
    setIsCreating(true);
    setActionError(null);
    try {
      await createShareLink({
        resourceId: reportId,
        userId: user.id,
        expiresIn,
        label: label.trim() || undefined,
      });
      setLabel('');
      await fetchLinks();
    } catch (err) {
      setActionError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopy = (link: ShareLinkRecord) => {
    if (!link.shareLink) {
      setActionError(new Error('Share link URL is unavailable for this record.'));
      return;
    }

    navigator.clipboard.writeText(link.shareLink).then(() => {
      setCopiedId(link.id);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {
      setActionError(new Error('Failed to copy link. Please copy it manually.'));
    });
  };

  const handleRevoke = async (id: string) => {
    if (!window.confirm('Revoke this share link? Anyone using it will immediately lose access. This cannot be undone.')) return;
    setRevokingId(id);
    try {
      await revokeShareLink(id);
      await fetchLinks();
    } catch (err) {
      setActionError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setRevokingId(null);
    }
  };

  const activeLinks = (links ?? []).filter((l) => l.status === 'active');
  const inactiveLinks = (links ?? []).filter((l) => l.status !== 'active');

  return (
    // Backdrop
    <div
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: withAlpha(T.black, 0.5),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
        padding: '16px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Modal panel */}
      <div
        style={{
          backgroundColor: T.white,
          borderRadius: '16px',
          width: '100%',
          maxWidth: '620px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: T.shadowLg,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '24px 28px 20px',
            borderBottom: `1px solid ${T.gray200}`,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: T.gray800 }}>
              Share Report
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: T.gray500 }}>
              {reportName}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '32px', height: '32px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: 'none', borderRadius: '8px',
              backgroundColor: T.gray100, cursor: 'pointer',
              fontSize: '18px', color: T.gray500,
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '24px 28px' }}>
          {/* ── Create new link ── */}
          <div
            style={{
              backgroundColor: T.gray50,
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px',
              border: `1px solid ${T.gray200}`,
            }}
          >
            <h4 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: 600, color: T.gray700 }}>
              Create new share link
            </h4>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
              {/* Label */}
              <div style={{ flex: '1 1 180px' }}>
                <label style={labelStyle}>Label (optional)</label>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Prospect: Acme Corp"
                  maxLength={120}
                  style={inputStyle}
                />
              </div>

              {/* Expiry */}
              <div style={{ flex: '0 1 160px' }}>
                <label style={labelStyle}>Expires in</label>
                <select
                  value={expiresIn}
                  onChange={(e) => setExpiresIn(e.target.value as ShareExpiry)}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  {EXPIRY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <ApiErrorNotice error={actionError} />

            <button
              onClick={handleCreate}
              disabled={isCreating}
              style={{
                padding: '8px 20px',
                fontSize: '14px',
                fontWeight: 600,
                color: T.white,
                backgroundColor: isCreating ? T.cobalt300 : T.cobalt500,
                border: 'none',
                borderRadius: '8px',
                cursor: isCreating ? 'default' : 'pointer',
                transition: 'background-color 0.15s',
              }}
            >
              {isCreating ? 'Creating…' : '+ Create Link'}
            </button>
          </div>

          {/* ── Error ── */}
          <ApiErrorNotice error={fetchError} />

          {/* ── Loading ── */}
          {isLoading && (
            <div style={{ textAlign: 'center', padding: '32px', color: T.gray400 }}>
              Loading links…
            </div>
          )}

          {/* ── Active links ── */}
          {!isLoading && (
            <>
              {activeLinks.length === 0 && inactiveLinks.length === 0 && (
                <div style={{ textAlign: 'center', padding: '24px', color: T.gray400, fontSize: '14px' }}>
                  No share links yet. Create one above.
                </div>
              )}

              {activeLinks.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 600, color: T.gray600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Active ({activeLinks.length})
                  </p>
                  {activeLinks.map((link) => (
                    <LinkRow
                      key={link.id}
                      link={link}
                      copiedId={copiedId}
                      revokingId={revokingId}
                      onCopy={handleCopy}
                      onRevoke={handleRevoke}
                    />
                  ))}
                </div>
              )}

              {inactiveLinks.length > 0 && (
                <div>
                  <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 600, color: T.gray400, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Expired / Revoked ({inactiveLinks.length})
                  </p>
                  {inactiveLinks.map((link) => (
                    <LinkRow
                      key={link.id}
                      link={link}
                      copiedId={copiedId}
                      revokingId={revokingId}
                      onCopy={handleCopy}
                      onRevoke={handleRevoke}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LinkRow sub-component
// ---------------------------------------------------------------------------

interface LinkRowProps {
  link: ShareLinkRecord;
  copiedId: string | null;
  revokingId: string | null;
  onCopy: (link: ShareLinkRecord) => void;
  onRevoke: (id: string) => void;
}

function LinkRow({ link, copiedId, revokingId, onCopy, onRevoke }: LinkRowProps) {
  const colors = SHARE_LINK_STATUS_COLORS[link.status] ?? SHARE_LINK_STATUS_COLORS.expired;
  const isActive = link.status === 'active';

  return (
    <div
      style={{
        border: `1px solid ${T.gray200}`,
        borderRadius: '10px',
        padding: '14px 16px',
        marginBottom: '10px',
        backgroundColor: T.white,
      }}
    >
      {/* Top row: label + status badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
        <StatusDot color={colors.dot} />
        <span style={{ flex: 1, fontSize: '14px', fontWeight: 500, color: T.gray800, wordBreak: 'break-word' }}>
          {link.label || <span style={{ color: T.gray400, fontStyle: 'italic' }}>No label</span>}
        </span>
        <span
          style={{
            padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 600,
            backgroundColor: colors.bg, color: colors.text, whiteSpace: 'nowrap',
          }}
        >
          {link.status.charAt(0).toUpperCase() + link.status.slice(1)}
        </span>
      </div>

      {/* URL preview */}
      <div
        style={{
          fontSize: '12px', fontFamily: T.fontMono, color: T.gray500,
          backgroundColor: T.gray50, borderRadius: '6px',
          padding: '6px 10px', marginBottom: '10px',
          overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={link.shareLink}
      >
        {link.shareLink}
      </div>

      {/* Meta + actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '12px', color: T.gray400, flex: 1 }}>
          Expires {formatDateTime(link.expiresAt)}
          {link.revokedAt && ` · Revoked ${formatDateTime(link.revokedAt)}`}
        </span>

        {/* Copy button — always available */}
        <button
          onClick={() => onCopy(link)}
          disabled={!link.shareLink}
          style={{
            padding: '4px 12px', fontSize: '12px', fontWeight: 500,
            color: copiedId === link.id ? T.green800 : T.sky600,
            backgroundColor: copiedId === link.id ? T.green100 : T.sky100,
            border: 'none', borderRadius: '6px', cursor: 'pointer',
            transition: 'all 0.2s',
            opacity: link.shareLink ? 1 : 0.6,
          }}
        >
          {copiedId === link.id ? '✓ Copied' : 'Copy Link'}
        </button>

        {/* Revoke — only for active links */}
        {isActive && (
          <button
            onClick={() => onRevoke(link.id)}
            disabled={revokingId === link.id}
            style={{
              padding: '4px 12px', fontSize: '12px', fontWeight: 500,
              color: T.error,
              backgroundColor: T.errorBg,
              border: 'none', borderRadius: '6px',
              cursor: revokingId === link.id ? 'default' : 'pointer',
              opacity: revokingId === link.id ? 0.6 : 1,
            }}
          >
            {revokingId === link.id ? 'Revoking…' : 'Revoke'}
          </button>
        )}
      </div>
    </div>
  );
}

function StatusDot({ color }: { color: string }) {
  return (
    <span
      style={{
        width: '8px', height: '8px',
        borderRadius: '50%',
        backgroundColor: color,
        display: 'inline-block',
        flexShrink: 0,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: T.gray600,
  marginBottom: '6px',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: '14px',
  border: `1px solid ${T.gray300}`,
  borderRadius: '8px',
  backgroundColor: T.white,
  color: T.gray700,
  boxSizing: 'border-box',
};
